/**
 * Maps internal canonical filter keys to the strings the v2 wire format uses.
 *
 * The internal canonical key is whatever `EFilterKeys` stores (e.g.
 * `EFilterKeys.PRODUCT_TYPE === 'productType'`). The v2 wire format ships
 * camel-case keys as lowercase, so the BFF emits and accepts `producttype`
 * for the same logical filter when v2 is the active format.
 *
 * Anything not listed here passes through untouched in both directions —
 * v1 keys, v2 keys that already match the canonical name (e.g. `colors`,
 * `subcollections`), and any unknown future key.
 *
 * Add a new entry whenever a v2 wire key diverges from its canonical form;
 * the four boundary functions in this file are the only places that need
 * to change.
 *
 * NOTE: keys here are intentionally string literals rather than
 * `EFilterKeys.*` references to avoid a circular-import init order with
 * `CollectionFilter.ts` (the enum is declared at the bottom of that file
 * and isn't ready when the alias map is evaluated). The
 * `every alias round-trips` test in `filter-key-aliases.test.ts` pins the
 * link to the enum, so any future enum-value drift is caught there.
 */
export const FILTER_KEY_V2_ALIASES: Readonly<Record<string, string>> = {
  // eslint-disable-next-line i18next/no-literal-string
  productType: 'producttype',
}

const FILTER_KEY_V2_REVERSE_ALIASES: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(FILTER_KEY_V2_ALIASES).map(([internal, wire]) => [wire, internal]),
)

/**
 * Convert an internal canonical filter key to the v2 wire-format key.
 * Returns the input unchanged when no alias exists.
 *
 * Used at v2 write boundaries: URL serializer, explicit `setV2Segment`
 * calls in `CollectionService` that bypass the serializer.
 */
export const toV2Key = (internalKey: string): string => FILTER_KEY_V2_ALIASES[internalKey] ?? internalKey

/**
 * Convert a v2 wire-format filter key back to the internal canonical form.
 * Returns the input unchanged when no alias exists.
 *
 * Used at v2 read boundaries: the URL parser in `CollectionFilter.ts`.
 */
export const fromV2Key = (wireKey: string): string => FILTER_KEY_V2_REVERSE_ALIASES[wireKey] ?? wireKey
